// Bots (docs/bots.md): an app whose manifest has a `bot` block. It's bloomed, published and rolled back like any app,
// keeps its data in its own collections and changes it only through its declared actions, but it also has its own
// chat identity (author id `bot-<ULID>`, set by Bloom) and triggers: things that happen in the server (a mention, a
// message, a reaction, a join, a time of day…) that run one of its actions in its Dynamic Worker.
//
// Who a run acts for (whose permissions it uses):
// - someone who addressed the bot (a mention, a DM, a command, opening its page): that person, exactly as if they
//   did it themselves ("Chef on behalf of Sam");
// - everything else (a message posted, a reaction, a join, a schedule): nobody. The bot then sees only shared things
//   in channels it was added to, and changes only its own app's data and its own replies.
//
// The rules about who may talk to a bot, add it to a channel, disable it or change its settings live here, in one
// place (canTalkTo, canAddBot, …): the room enforces them and clients use them to decide what to offer.
import { cleanName } from "./cleanName.ts";
import type { Role } from "./protocol.ts";

/** Bot ids: "bot-" + a ULID. Better Auth ids are 32 characters and "bloom" is Bloom's, so nothing clashes. */
export const BOT_ID = /^bot-[0-9A-HJKMNP-TV-Z]{26}$/;
export const isBotId = (id: string | null | undefined): boolean => typeof id === "string" && BOT_ID.test(id);

export type BotTriggerKind =
  | "mention"
  | "dm"
  | "message"
  | "messageEdited"
  | "reaction"
  | "reactionRemoved"
  | "threadCreated"
  | "memberJoin"
  | "recordChanged"
  | "daily"
  | "appOpen";

export const BOT_TRIGGER_KINDS: readonly BotTriggerKind[] = ["mention", "dm", "message", "messageEdited", "reaction", "reactionRemoved", "threadCreated", "memberJoin", "recordChanged", "daily", "appOpen"];

/** Triggers where someone addressed the bot: the run acts for them. The rest act for nobody. */
export const ADDRESSED_TRIGGERS: readonly BotTriggerKind[] = ["mention", "dm", "appOpen"];

/** What a message must look like to fire a `message` (or `messageEdited`) trigger. All given parts must hold. */
export interface MessageMatch {
  /** The text starts with this (case-insensitive), e.g. "!roll". */
  startsWith?: string;
  /** The text has at least one of these words or phrases (case-insensitive, whole words). */
  contains?: string[];
  /** Only from these roles. */
  fromRoles?: Role[];
  /** It has at least one of these: a picture, a video, another file, a link. */
  has?: ("image" | "video" | "file" | "link")[];
  /** With has "link": only links to these domains (and their subdomains). */
  linkDomains?: string[];
}

export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
export const WEEKDAYS: readonly Weekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export type BotTrigger =
  | { on: "mention" | "dm" | "threadCreated" | "memberJoin" | "appOpen"; action: string }
  | { on: "message" | "messageEdited"; action: string; match?: MessageMatch }
  /** `count`: fires once, when a message reaches this many of the emoji (a starboard at 3 ⭐). Default: every add. */
  | { on: "reaction"; action: string; emoji?: string[]; count?: number }
  | { on: "reactionRemoved"; action: string; emoji?: string[] }
  /** A record in the bot's own collections was written by someone (not by the bot's own runs). */
  | { on: "recordChanged"; action: string; collection?: string }
  /** "HH:MM" in the server's time zone (America/New_York for now), on these days (default every day). */
  | { on: "daily"; action: string; at: string; days?: Weekday[] };

export interface BotSpec {
  /** The name it posts under, and is @mentioned by ("Chef"). */
  name: string;
  /** One emoji: its avatar. */
  avatar: string;
  triggers: BotTrigger[];
}

/** Who may talk to a bot (trigger it, DM it, use its commands). The owner sets it; new bots start at `everyone`. */
export type BotAccess =
  | { who: "everyone" }
  | { who: "roles"; roles: Role[] }
  | { who: "people"; people: string[] }
  | { who: "private" };

export const MAX_BOT_PEOPLE = 50;
export const MAX_BOT_TRIGGERS = 12;
export const MAX_BOT_NAME = 32;
export const BOT_TIME_ZONE = "America/New_York";

/** A bot as clients see it (ready.bots and "bot" events). */
export interface BotInfo {
  /** The app's slug. */
  app: string;
  /** Its author id. */
  id: string;
  name: string;
  avatar: string;
  ownerId: string;
  access: BotAccess;
  /** Switched off by its owner or a server owner/admin: no triggers, no replies, commands hidden. */
  disabled: boolean;
  /** Why it's off when it turned itself off ("5 runs in a row failed: …", "its owner left"). */
  pausedReason?: string;
  /** Text channels it was added to (passive triggers fire only there). */
  channels: string[];
  /** Which kinds of trigger it has (for the About tab). */
  triggers: BotTriggerKind[];
}

/** Someone acting on a bot's rules (a person, or an AI acting for one: same id and role). */
export interface BotActor {
  userId: string;
  role: Role;
}

const moderates = (a: BotActor) => a.role === "owner" || a.role === "admin";

/** May `a` talk to the bot (trigger it by addressing it, DM it, run its commands)? */
export function canTalkTo(b: Pick<BotInfo, "access" | "ownerId" | "disabled">, a: BotActor): boolean {
  if (b.disabled) return false;
  if (a.userId === b.ownerId) return true;
  switch (b.access.who) {
    case "everyone":
      return true;
    case "roles":
      return b.access.roles.includes(a.role);
    case "people":
      return b.access.people.includes(a.userId);
    default:
      return false;
  }
}

/** May `a` add the bot to a text channel? Its owner, or a server owner or admin. Not while only its owner talks to it. */
export function canAddBot(b: Pick<BotInfo, "access" | "ownerId">, a: BotActor): boolean {
  if (b.access.who === "private") return false;
  return a.userId === b.ownerId || moderates(a);
}

/** May `a` take the bot out of a channel? A server owner or admin, or the bot's owner. */
export function canRemoveBot(b: Pick<BotInfo, "ownerId">, a: BotActor): boolean {
  return a.userId === b.ownerId || moderates(a);
}

/** May `a` switch the bot off or on? Its owner, or a server owner or admin (who can't change it otherwise). */
export function canDisableBot(b: Pick<BotInfo, "ownerId">, a: BotActor): boolean {
  return a.userId === b.ownerId || moderates(a);
}

/** May `a` change the bot (prompt it, publish, roll back, restore its data, set who may talk to it)? Only its owner. */
export function canEditBot(b: Pick<BotInfo, "ownerId">, a: BotActor): boolean {
  return a.userId === b.ownerId;
}

/**
 * May `a` use this app at all (its commands, its data, its page)? Every app but a bot: yes. A bot: whoever may talk to
 * it; while it's switched off (or not set up yet), only its owner. The room (AppRuntime.canUse) and clients both ask.
 */
export function appUsableBy(app: { bot?: unknown; bloom?: { botId?: string; owner: string } }, bots: Pick<BotInfo, "id" | "access" | "ownerId" | "disabled">[], a: BotActor): boolean {
  if (!app.bot) return true;
  const b = app.bloom?.botId ? bots.find((x) => x.id === app.bloom!.botId) : undefined;
  if (!b) return a.userId === app.bloom?.owner;
  if (b.disabled) return a.userId === b.ownerId;
  return canTalkTo(b, a);
}

/**
 * What someone who may not use a bot gets of its app (ready.apps, "apps" events): who it is, never its commands,
 * collections, actions or code, and while only its owner talks to it, its description stays theirs too.
 */
export function redactBotApp<T extends { slug: string; name: string; icon: string; description: string; version: number; bot?: { name: string; avatar: string }; bloom?: { owner: string; botId?: string; noView?: boolean; publishedAt?: number } }>(app: T, bot: Pick<BotInfo, "access"> | undefined): T {
  return {
    slug: app.slug,
    name: app.name,
    icon: app.icon,
    description: bot?.access.who === "private" || !bot ? "Only its owner talks to it." : app.description,
    version: app.version,
    commands: [],
    collections: {},
    actions: {},
    ...(app.bot ? { bot: { name: app.bot.name, avatar: app.bot.avatar, triggers: [] } } : {}),
    ...(app.bloom ? { bloom: { bundle: "", thread: "", owner: app.bloom.owner, editors: [], ...(app.bloom.botId ? { botId: app.bloom.botId } : {}), noView: true, ...(app.bloom.publishedAt ? { publishedAt: app.bloom.publishedAt } : {}) } } : {}),
  } as unknown as T;
}

/** A cleaned BotAccess (unknown shapes become "everyone"; people only known ids, at most MAX_BOT_PEOPLE). */
export function cleanAccess(raw: unknown, isMember: (id: string) => boolean = () => true): BotAccess {
  const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  if (r.who === "private") return { who: "private" };
  if (r.who === "roles" && Array.isArray(r.roles)) {
    const roles = [...new Set(r.roles.filter((x): x is Role => x === "owner" || x === "admin" || x === "member"))];
    if (roles.length) return { who: "roles", roles };
  }
  if (r.who === "people" && Array.isArray(r.people)) {
    const people = [...new Set(r.people.filter((x): x is string => typeof x === "string" && isMember(x)))].slice(0, MAX_BOT_PEOPLE);
    return { who: "people", people };
  }
  return { who: "everyone" };
}

/** "Everyone", "Owners and admins", "3 people", "Only Sam" (with the owner's name). */
export function accessLabel(a: BotAccess, ownerName = "its owner"): string {
  if (a.who === "private") return `Only ${ownerName}`;
  if (a.who === "people") return a.people.length ? `${ownerName} and ${a.people.length} ${a.people.length === 1 ? "person" : "people"}` : `Only ${ownerName}`;
  if (a.who === "roles") return a.roles.map((r) => (r === "owner" ? "owners" : r === "admin" ? "admins" : "members")).join(" and ").replace(/^./, (c) => c.toUpperCase());
  return "Everyone";
}

// ---- Matching (in the room, after the ack; no regex from apps) ----

const WORD = /[\p{L}\p{N}_]/u;

/** Is `phrase` in `text` as whole words (case-insensitive)? */
function hasPhrase(text: string, phrase: string): boolean {
  const t = text.toLowerCase();
  const p = phrase.toLowerCase().trim();
  if (!p) return false;
  for (let i = t.indexOf(p); i !== -1; i = t.indexOf(p, i + 1)) {
    const before = t.charAt(i - 1);
    const after = t.charAt(i + p.length);
    if ((!before || !WORD.test(before)) && (!after || !WORD.test(after))) return true;
  }
  return false;
}

export interface MatchSubject {
  /** Plain text (mentions as names). */
  text: string;
  role: Role;
  attachments: { kind: "image" | "video" | "file" }[];
  links: string[];
}

/** Does a message fire a `message` trigger with this match? */
export function matchMessage(m: MessageMatch | undefined, s: MatchSubject): boolean {
  if (!m) return true;
  if (m.startsWith && !s.text.trimStart().toLowerCase().startsWith(m.startsWith.toLowerCase())) return false;
  if (m.contains?.length && !m.contains.some((p) => hasPhrase(s.text, p))) return false;
  if (m.fromRoles?.length && !m.fromRoles.includes(s.role)) return false;
  if (m.has?.length) {
    const ok = m.has.some((h) => {
      if (h === "link") {
        if (!m.linkDomains?.length) return s.links.length > 0;
        return s.links.some((l) => {
          try {
            const host = new URL(l).hostname.toLowerCase();
            return m.linkDomains!.some((d) => host === d.toLowerCase() || host.endsWith(`.${d.toLowerCase()}`));
          } catch {
            return false;
          }
        });
      }
      return s.attachments.some((a) => a.kind === h);
    });
    if (!ok) return false;
  }
  return true;
}

// ---- Daily triggers ----

/** The wall-clock parts of `t` in `tz`. */
function zoned(t: number, tz: string) {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short" }).formatToParts(new Date(t));
  const get = (k: string) => parts.find((p) => p.type === k)?.value ?? "";
  return { y: Number(get("year")), mo: Number(get("month")), d: Number(get("day")), h: Number(get("hour")), mi: Number(get("minute")), wd: get("weekday").toLowerCase().slice(0, 3) as Weekday };
}

/** The UTC time of a wall-clock moment in `tz` (searching the offset, so daylight saving is right). */
function fromZoned(y: number, mo: number, d: number, h: number, mi: number, tz: string): number {
  let guess = Date.UTC(y, mo - 1, d, h, mi);
  for (let i = 0; i < 3; i++) {
    const z = zoned(guess, tz);
    const diff = Date.UTC(z.y, z.mo - 1, z.d, z.h, z.mi) - Date.UTC(y, mo - 1, d, h, mi);
    if (!diff) break;
    guess -= diff;
  }
  return guess;
}

export const DAILY_AT = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** The next time after `after` that a daily trigger fires ("HH:MM" in `tz`, on `days`). */
export function nextDaily(at: string, days: Weekday[] | undefined, after: number, tz = BOT_TIME_ZONE): number {
  const m = DAILY_AT.exec(at);
  if (!m) return Number.POSITIVE_INFINITY;
  const [h, mi] = [Number(m[1]), Number(m[2])];
  const z = zoned(after, tz);
  for (let i = 0; i <= 8; i++) {
    const day = new Date(Date.UTC(z.y, z.mo - 1, z.d + i));
    const t = fromZoned(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), h, mi, tz);
    if (t <= after) continue;
    const wd = zoned(t, tz).wd;
    if (days?.length && !days.includes(wd)) continue;
    return t;
  }
  return Number.POSITIVE_INFINITY;
}

// ---- What a run gets (ctx.event) ----

export interface BotPerson {
  id: string;
  name: string;
}

export interface BotEventMessage {
  id: string;
  /** Plain text: mentions and channels as names, custom emoji as :name:. */
  text: string;
  author: BotPerson & { bot?: boolean };
  /** Where: a text channel's id and name, or a DM (name null); `thread` when it's in a thread. */
  channel: { id: string; name: string | null; thread: boolean };
  attachments: { name: string; kind: "image" | "video" | "file"; size: number }[];
  links: string[];
  replyTo?: { author: string; text: string };
  at: number;
}

export interface BotEvent {
  on: BotTriggerKind | "command" | "schedule" | "later";
  /** Who the run acts for, or null (nobody: it addressed no one). */
  actsFor: BotPerson | null;
  message?: BotEventMessage;
  reaction?: { emoji: string; by: BotPerson; count: number };
  member?: BotPerson;
  record?: { collection: string; id: string; before: Record<string, unknown> | null; after: Record<string, unknown> | null; by: BotPerson };
  /** ctx.later: what the earlier run handed over. */
  data?: unknown;
  /** The channels the bot was added to (where ctx.reply/postCard may go with `to`). */
  channels: { id: string; name: string }[];
}

/** One reply a run asked for: plain text (markdown), in the trigger's conversation or `to` (an added channel). */
export interface BotReply {
  text: string;
  to?: string;
  /** Only the person the run acts for sees it (a private card), never a message. */
  private?: boolean;
  /** One of the server's stickers, by name (ignoring case), posted with the text (which may then be empty). */
  sticker?: string;
}

/** One ctx.later a run asked for. */
export interface BotLater {
  /** Minutes from now: 1 to 43200 (30 days). */
  inMinutes: number;
  action: string;
  data?: unknown;
}

export const MAX_REPLIES_PER_RUN = 3;
export const MAX_REACTS_PER_RUN = 3;
export const MAX_LATERS_PER_RUN = 5;
export const MAX_PENDING_LATERS = 100;
export const MAX_LATER_MINUTES = 43_200;
export const MAX_BOT_REPLY = 2000;
/** Runs one bot may make in a server per minute; past it, triggers are dropped (and its About tab says so). */
export const BOT_RUNS_PER_MINUTE = 60;
/** Replies one bot may post per conversation per minute (past it they're dropped: a loop or a flood stops there). */
export const BOT_REPLIES_PER_MINUTE = 10;
/** Crashes in a row (not refusals) that switch a bot off until its owner or a moderator turns it back on. */
export const BOT_FAILURES_TO_PAUSE = 5;

// ---- Checks (validateManifest calls this for manifests with a bot block) ----

export function botProblems(bot: unknown, actions: Record<string, { inputs: { required?: boolean; default?: unknown }[]; confirm?: boolean }> | null, collections: string[]): string[] {
  const errs: string[] = [];
  if (!bot || typeof bot !== "object" || Array.isArray(bot)) return ["bot must be an object: { name, avatar, triggers }"];
  const b = bot as BotSpec;
  if (typeof b.name !== "string" || !b.name.trim() || b.name.length > MAX_BOT_NAME) errs.push(`bot.name must be 1–${MAX_BOT_NAME} characters`);
  else {
    // The bot is stored under the cleaned name: NFKC alone is no problem, and the plain checks read the cleaned name.
    const name = cleanName(b.name);
    if (name !== b.name.normalize("NFKC")) errs.push(`bot.name can't have control, direction or zero-width characters, or extra spaces: use "${name}"`);
    else if (name.length > MAX_BOT_NAME) errs.push(`bot.name must be 1–${MAX_BOT_NAME} characters (after NFKC normalization)`);
    else if (/[<>@#:*`\\]/.test(name)) errs.push("bot.name can't contain < > @ # : * ` or \\");
    else if (name.toLowerCase() === "bloom" || name.toLowerCase() === "everyone") errs.push(`bot.name can't be "${name}"`);
  }
  if (typeof b.avatar !== "string" || !b.avatar.trim() || [...b.avatar].length > 4) errs.push("bot.avatar must be one emoji");
  if (!Array.isArray(b.triggers)) return [...errs, "bot.triggers must be a list (it can be empty: then only commands run it)"];
  if (b.triggers.length > MAX_BOT_TRIGGERS) errs.push(`at most ${MAX_BOT_TRIGGERS} triggers`);
  const seen = new Set<string>();
  for (const [i, t] of b.triggers.entries()) {
    const at = `bot.triggers[${i}]`;
    if (!t || typeof t !== "object" || !BOT_TRIGGER_KINDS.includes(t.on)) {
      errs.push(`${at}.on must be one of ${BOT_TRIGGER_KINDS.join(", ")}`);
      continue;
    }
    const spec = actions && typeof t.action === "string" && Object.hasOwn(actions, t.action) ? actions[t.action] : undefined;
    if (!spec) errs.push(`${at} runs action "${String(t.action)}", which isn't declared`);
    else if (spec.inputs.some((x) => x.required && x.default === undefined)) errs.push(`${at}: action "${t.action}" can't have required inputs (a trigger fills none in; read ctx.event)`);
    else if (spec.confirm) errs.push(`${at}: action "${t.action}" is marked confirm (destructive), so nothing may run it on its own; give the trigger a different action`);
    const key = t.on === "daily" ? `daily:${(t as { at?: string }).at}` : t.on === "message" || t.on === "messageEdited" || t.on === "reaction" || t.on === "reactionRemoved" || t.on === "recordChanged" ? `${t.on}:${i}` : t.on;
    if (seen.has(key)) errs.push(`${at}: "${t.on}" is declared twice`);
    seen.add(key);
    if (t.on === "message" || t.on === "messageEdited") {
      const m = t.match;
      if (m !== undefined) {
        if (!m || typeof m !== "object") errs.push(`${at}.match must be an object`);
        else {
          if (m.startsWith !== undefined && (typeof m.startsWith !== "string" || !m.startsWith.trim() || m.startsWith.length > 40)) errs.push(`${at}.match.startsWith must be 1–40 characters`);
          if (m.contains !== undefined && (!Array.isArray(m.contains) || !m.contains.length || m.contains.length > 20 || m.contains.some((x) => typeof x !== "string" || !x.trim() || x.length > 40))) errs.push(`${at}.match.contains must be 1–20 words or phrases (each ≤ 40 characters)`);
          if (m.fromRoles !== undefined && (!Array.isArray(m.fromRoles) || m.fromRoles.some((r) => !["owner", "admin", "member"].includes(r)))) errs.push(`${at}.match.fromRoles must list owner, admin or member`);
          if (m.has !== undefined && (!Array.isArray(m.has) || m.has.some((h) => !["image", "video", "file", "link"].includes(h)))) errs.push(`${at}.match.has must list image, video, file or link`);
          if (m.linkDomains !== undefined && (!Array.isArray(m.linkDomains) || m.linkDomains.length > 10 || m.linkDomains.some((d) => typeof d !== "string" || !/^[a-z0-9.-]{3,80}$/i.test(d)))) errs.push(`${at}.match.linkDomains must be up to 10 domains like "github.com"`);
        }
      }
      // Without a filter, a bot runs on every message in its channels; allowed, but at a cost (see docs/bots.md).
    }
    if (t.on === "reaction" || t.on === "reactionRemoved") {
      if (t.emoji !== undefined && (!Array.isArray(t.emoji) || !t.emoji.length || t.emoji.length > 10 || t.emoji.some((e) => typeof e !== "string" || !e || e.length > 64))) errs.push(`${at}.emoji must be 1–10 emoji`);
      if (t.on === "reaction" && t.count !== undefined && (!Number.isInteger(t.count) || t.count < 1 || t.count > 100)) errs.push(`${at}.count must be a whole number from 1 to 100`);
    }
    if (t.on === "recordChanged" && t.collection !== undefined && !collections.includes(t.collection)) errs.push(`${at}.collection "${t.collection}" isn't declared`);
    if (t.on === "daily") {
      if (typeof t.at !== "string" || !DAILY_AT.test(t.at)) errs.push(`${at}.at must be "HH:MM" (24-hour, ${BOT_TIME_ZONE})`);
      if (t.days !== undefined && (!Array.isArray(t.days) || !t.days.length || t.days.some((d) => !WEEKDAYS.includes(d)))) errs.push(`${at}.days must list ${WEEKDAYS.join(", ")}`);
    }
  }
  if (b.triggers.filter((t) => t?.on === "daily").length > 3) errs.push("at most 3 daily triggers");
  return errs;
}
